import React from 'react';
import { useFormik } from 'formik';
import { Link as LinkRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import { Alert, Box, Button, TextField, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useYupObject } from 'hooks';
import PasswordField from 'components/PasswordField';
import { LoginVariables } from 'types/Login';

function LoginAccountForm({
  handleLogin,
  postingLogin,
  errorPostingLogin,
}: {
  handleLogin: (options: LoginVariables) => void;
  postingLogin: boolean;
  errorPostingLogin?: ApolloError;
}) {
  const yup = useYupObject();
  const { t } = useTranslation(['login', 'api_errors']);

  const validationSchema = yup.object({
    masterCompanyId: yup.number().required(),
    accountId: yup.number().required().moreThan(0),
    password: yup.string().required().trim(),
  });

  const initialValues = {
    masterCompanyId: '',
    accountId: '',
    password: '',
  };

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: ({ masterCompanyId, accountId, password }) => {
      handleLogin({ email: `${masterCompanyId};${accountId}`, password });
    },
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 5 }}>
      <Box sx={{ width: '100%', display: 'inline-flex' }}>
        <TextField
          label={t('login:master_company_id')}
          name="masterCompanyId"
          id="masterCompanyId"
          autoFocus
          fullWidth
          placeholder={t('login:master_company_id')}
          helperText={formik.touched.masterCompanyId && formik.errors.masterCompanyId}
          value={formik.values.masterCompanyId}
          onChange={formik.handleChange}
          error={formik.touched.masterCompanyId && Boolean(formik.errors.masterCompanyId)}
          margin="normal"
          sx={{ mr: 3 }}
          autoComplete="off"
        />
        <TextField
          label={t('login:account_id')}
          name="accountId"
          id="accountId"
          fullWidth
          placeholder={t('login:account_id')}
          helperText={formik.touched.accountId && formik.errors.accountId}
          value={formik.values.accountId}
          onChange={formik.handleChange}
          error={formik.touched.accountId && Boolean(formik.errors.accountId)}
          margin="normal"
          autoComplete="off"
        />
      </Box>
      <PasswordField
        label={t('login:password')}
        name="password"
        id="password"
        fullWidth
        placeholder={t('login:password')}
        helperText={formik.touched.password && formik.errors.password}
        value={formik.values.password}
        onChange={formik.handleChange}
        error={formik.touched.password && Boolean(formik.errors.password)}
        margin="normal"
        autoComplete="off"
      />

      {errorPostingLogin && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t(
            `api_errors:${
              errorPostingLogin.message as
                | 'invalid_username_or_password'
                | 'user_is_already_logged_in_another_device'
            }`,
            t('api_errors:generic_error'),
          )}
        </Alert>
      )}

      <LoadingButton
        loading={postingLogin}
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mb: 2, mt: 4 }}
      >
        {t('login:login')}
      </LoadingButton>
      <Link to="/recover-password" component={LinkRouter} sx={{ textDecoration: 'none' }}>
        <Button fullWidth size="large">
          {t('login:i_forgot_my_password')}
        </Button>
      </Link>
    </Box>
  );
}

export default LoginAccountForm;
